/**
 * EmptyState — V2.0 Foundation primitive
 *
 * PHASE 4 REDESIGN: Migrated off `--v2-*` Tailwind utilities onto the
 * design-system `--ds-*` CSS variables. Every prop, test-id, and
 * rendered text position is preserved verbatim.
 *
 * A meaningful empty state — friendly heading, description, optional
 * illustration, and up to two CTAs. Renders without an illustration if none
 * is provided (text-first layout).
 *
 * Test IDs (UNCHANGED):
 *   v2-empty-state, v2-empty-illustration, v2-empty-heading,
 *   v2-empty-description, v2-empty-primary-cta, v2-empty-secondary-cta
 */
import React from "react";
import clsx from "clsx";

function Cta({ testId, cta, variant }) {
  if (!cta) return null;
  const { label, onClick, href, icon: Icon } = cta;
  const base =
    "inline-flex items-center justify-center gap-2 rounded-[var(--ds-radius-md)] px-4 py-2 text-[13px] font-semibold transition-colors";
  const styles =
    variant === "primary"
      ? "bg-[var(--ds-color-accent)] text-[var(--ds-color-accent-ink)] hover:bg-[var(--ds-color-accent-strong)] shadow-[var(--ds-shadow-card)]"
      : "text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)]";
  const inner = (
    <>
      {Icon ? <Icon className="h-4 w-4" aria-hidden="true" /> : null}
      <span>{label}</span>
    </>
  );
  if (href) {
    return (
      <a
        data-testid={testId}
        href={href}
        onClick={onClick}
        className={clsx(base, styles)}
        style={{ transitionDuration: "var(--ds-duration-fast)" }}
      >
        {inner}
      </a>
    );
  }
  return (
    <button
      data-testid={testId}
      type="button"
      onClick={onClick}
      className={clsx(base, styles)}
      style={{ transitionDuration: "var(--ds-duration-fast)" }}
    >
      {inner}
    </button>
  );
}

export default function EmptyState({
  illustration,
  heading,
  description,
  primaryCta,
  secondaryCta,
}) {
  return (
    <div
      data-testid="v2-empty-state"
      className="rounded-[var(--ds-radius-xl)] bg-[var(--ds-color-surface)] border border-[var(--ds-color-border)] shadow-[var(--ds-shadow-card)] px-6 py-12 md:px-10 md:py-16 flex flex-col items-center text-center gap-5"
    >
      {illustration ? (
        <div
          data-testid="v2-empty-illustration"
          className="max-w-[280px] w-full mb-2 opacity-95"
        >
          {illustration}
        </div>
      ) : null}
      <h3
        data-testid="v2-empty-heading"
        className="text-[22px] leading-tight text-[var(--ds-color-ink)]"
        style={{ fontFamily: "var(--ds-font-display)", fontWeight: 600 }}
      >
        {heading}
      </h3>
      <p
        data-testid="v2-empty-description"
        className="text-[15px] leading-relaxed text-[var(--ds-color-ink-muted)] max-w-md"
      >
        {description}
      </p>
      {(primaryCta || secondaryCta) && (
        <div className="mt-2 flex flex-col sm:flex-row items-center gap-3">
          <Cta testId="v2-empty-primary-cta"   cta={primaryCta}   variant="primary" />
          <Cta testId="v2-empty-secondary-cta" cta={secondaryCta} variant="secondary" />
        </div>
      )}
    </div>
  );
}
